import moment from 'moment';
import { startOfDay, addDays } from 'date-fns';
import { PatientService } from '@/services/patient.service';
import { DoctorService } from '@/services/doctor.service';
import { REASSIGNMENT_DISABLED_IN_OPTIONS } from '@/constants/doctors';
import { REASSIGNMENT_TYPE } from '@/constants/ooo';
import BaseDateTimePicker from '@/components/base/BaseDateTimePicker.vue';
export default {
  components: {
    BaseDateTimePicker
  },
  props: {
    rowData: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  data() {
    return {
      showDialog: false,
      showUnsavedDialog: false,
      alert: {
        show: false,
        message: ''
      },
      doctorsInState: [],
      selectedDoctor: '',
      isProcessing: false,
      reassignmentType: REASSIGNMENT_TYPE.PERMANENT,
      reassignmentTypeOptions: [{
        value: REASSIGNMENT_TYPE.PERMANENT,
        text: 'Permanent'
      }, {
        value: REASSIGNMENT_TYPE.TEMPORARY,
        text: 'Temporary'
      }],
      untilRange: {
        start: null,
        end: null
      },
      patientState: null,
      patientDoctorId: null,
      dialogReady: false,
      fetchVersion: 0,
      confirmStep: 'form'
    };
  },
  computed: {
    isTemporary() {
      return this.reassignmentType === REASSIGNMENT_TYPE.TEMPORARY;
    },
    showConfirmModal: {
      get() {
        return this.confirmStep !== 'form';
      },
      set(value) {
        if (!value) {
          this.confirmStep = 'form';
        }
      }
    },
    selectedDoctorObj() {
      return this.doctorsInState.find(doctor => doctor.id === this.selectedDoctor) || null;
    },
    selectedDoctorLabel() {
      return this.selectedDoctorObj ? `Dr ${this.selectedDoctorObj.firstname} ${this.selectedDoctorObj.lastname}` : '';
    },
    reassignmentTypeLabel() {
      return this.isTemporary ? 'Temporary' : 'Permanent';
    },
    successMessage() {
      const patientName = `${this.rowData.firstname} ${this.rowData.lastname}`;
      if (this.isTemporary) {
        return `${patientName} has been reassigned to ${this.selectedDoctorLabel}, from Now (${this.startDateLabel}) until ${this.untilLabel}.`;
      }
      return `${patientName} has been reassigned to ${this.selectedDoctorLabel} on a permanent basis.`;
    },
    today() {
      return startOfDay(new Date());
    },
    untilPresets() {
      return [{
        label: 'Now',
        value: 'now',
        range: ({
          today
        }) => ({
          start: today,
          end: today
        })
      }, {
        label: 'In 2 days',
        value: 'in2days',
        range: ({
          today
        }) => ({
          start: today,
          end: addDays(today, 2)
        })
      }, {
        label: 'In 4 days',
        value: 'in4days',
        range: ({
          today
        }) => ({
          start: today,
          end: addDays(today, 4)
        })
      }, {
        label: 'In a week',
        value: 'inWeek',
        range: ({
          today
        }) => ({
          start: today,
          end: addDays(today, 7)
        })
      }, {
        label: 'In a month',
        value: 'inMonth',
        range: ({
          today
        }) => ({
          start: today,
          end: addDays(today, 30)
        })
      }];
    },
    startDateLabel() {
      return moment().format('MMM D, YYYY, h:mm A');
    },
    untilLabel() {
      return this.untilRange.end ? this.buildUntilMoment().format('MMM D, YYYY, h:mm A') : 'Select date';
    },
    untilDateKey() {
      return this.untilRange.end ? this.untilRange.end.getTime() : null;
    },
    hasUnsavedChanges() {
      return !!this.selectedDoctor || this.reassignmentType !== REASSIGNMENT_TYPE.PERMANENT;
    }
  },
  watch: {
    async showDialog(show) {
      this.$emit('dialog-toggle', show);
      if (show) {
        this.dialogReady = false;
        this.confirmStep = 'form';
        const patientData = await PatientService.getPatient(this.rowData.id);
        this.patientState = patientData.data.state;
        this.patientDoctorId = patientData.data.doctorid;
        this.resetForm();
        await this.fetchDoctors();
        this.dialogReady = true;
      } else {
        this.doctorsInState = [];
        this.alert.show = false;
        this.selectedDoctor = '';
        this.patientState = null;
        this.patientDoctorId = null;
        this.dialogReady = false;
        this.confirmStep = 'form';
      }
    },
    async reassignmentType() {
      if (!this.showDialog || !this.dialogReady) {
        return;
      }
      await this.fetchDoctors();
    },
    async untilDateKey() {
      if (!this.showDialog || !this.dialogReady || !this.isTemporary) {
        return;
      }
      await this.fetchDoctors();
    }
  },
  methods: {
    resetForm() {
      this.reassignmentType = REASSIGNMENT_TYPE.PERMANENT;
      this.selectedDoctor = '';
      this.untilRange = {
        start: this.today,
        end: addDays(this.today, 2)
      };
    },
    showAlert(msg) {
      this.alert.message = msg;
      this.alert.show = true;
    },
    getStates(doctor) {
      const states = doctor.coveredstate.map(coveredState => coveredState.state);
      return `State(s): ${states.join(', ')}`;
    },
    onUntilInput(range) {
      const end = range.end || range.start;
      this.untilRange = {
        start: this.today,
        end
      };
    },
    buildUntilMoment() {
      const now = moment();
      return moment(this.untilRange.end).set({
        hour: now.hour(),
        minute: now.minute(),
        second: now.second()
      });
    },
    async fetchDoctors() {
      if (!this.patientState) {
        return;
      }
      let enddatestamp;
      let startdatestamp;
      let reassignmenttype;
      if (this.isTemporary) {
        startdatestamp = moment().valueOf();
        enddatestamp = this.buildUntilMoment().valueOf();
        reassignmenttype = undefined;
      } else {
        enddatestamp = Date.now();
        startdatestamp = undefined;
        reassignmenttype = REASSIGNMENT_TYPE.PERMANENT;
      }
      this.fetchVersion += 1;
      const version = this.fetchVersion;
      const {
        data
      } = await DoctorService.getDoctorsByStateV2([this.patientState], enddatestamp, startdatestamp, REASSIGNMENT_DISABLED_IN_OPTIONS.GENERAL_PATIENT_TRANSFER, reassignmenttype);
      if (version !== this.fetchVersion) {
        return;
      }
      const blocked = this.isTemporary ? [REASSIGNMENT_DISABLED_IN_OPTIONS.GENERAL_PATIENT_TRANSFER] : [REASSIGNMENT_DISABLED_IN_OPTIONS.GENERAL_PATIENT_TRANSFER, REASSIGNMENT_DISABLED_IN_OPTIONS.PERMANENT_REASSIGNMENT];
      this.doctorsInState = data.filter(doctor => doctor.id !== this.patientDoctorId && (doctor.override_reassignment_rule || !blocked.some(option => {
        var _doctor$reassignment_;
        return (_doctor$reassignment_ = doctor.reassignment_disabled_in) === null || _doctor$reassignment_ === void 0 ? void 0 : _doctor$reassignment_.includes(option);
      })));
      if (this.selectedDoctor && !this.doctorsInState.some(doctor => doctor.id === this.selectedDoctor)) {
        this.selectedDoctor = '';
      }
    },
    openConfirmStep() {
      if (this.isProcessing) {
        return;
      }
      if (!this.selectedDoctor) {
        this.showAlert('Please select a doctor.');
        return;
      }
      this.alert.show = false;
      this.confirmStep = 'confirm';
    },
    async submitReassignment() {
      if (this.isProcessing) {
        return;
      }
      const body = {
        doctorid: this.selectedDoctor,
        patientid: this.rowData.id,
        assignmenttype: this.reassignmentType
      };
      if (this.isTemporary) {
        const startdatestamp = moment().utc().unix();
        const enddatestamp = this.buildUntilMoment().utc().unix();
        if (enddatestamp <= startdatestamp) {
          this.showAlert('Reassign until date must be after the start date.');
          return;
        }
        body.enddatestamp = enddatestamp;
      }
      this.isProcessing = true;
      try {
        const {
          data
        } = await DoctorService.reassignPatient(body);
        if (data) {
          this.alert.show = false;
          this.$emit('refresh-self');
          this.confirmStep = 'success';
        }
      } catch (err) {
        this.showAlert(err);
      } finally {
        this.isProcessing = false;
      }
    },
    finishSuccess() {
      this.$emit('show-toast', 'Patient reassigned successfully!', 'success');
      this.resetForm();
      this.showDialog = false;
    },
    handleClose(evt) {
      if (this.hasUnsavedChanges) {
        if (evt && typeof evt.preventDefault === 'function') {
          evt.preventDefault();
        }
        this.showUnsavedDialog = true;
      } else {
        this.showDialog = false;
      }
    },
    confirmClose() {
      this.showUnsavedDialog = false;
      this.resetForm();
      this.showDialog = false;
    }
  }
};